import { carpetOrderSummary } from './carpet-layout.js';
import { freeCarpetRemovalEligible } from './customer-proposal.js';
import { ProposalPricing } from './base-pricing.js';
const round = (n) => Math.round(n * 100) / 100;
const defaultId = (kind, productId) => `carpet-default-${kind}:${productId}`;
/** Apply the ordinary carpet package in one state change when its main product is chosen. */
export function withCarpetDefaults(doc, pricing, productId, catalogue, m) {
    const selection = pricing.products.find(p => p.productId === productId);
    if (!selection)
        return { ...doc, pricing };
    pricing = ProposalPricing.parse({ ...pricing, products: pricing.products.map(p => p.productId === productId ? { ...p, area: round(m.floorAreaM2) } : p) });
    const accessories = [...doc.accessories];
    const add = (kind, name, quantity, type, variantId) => {
        const id = defaultId(kind, productId);
        if (accessories.some(a => a.id === id || a.kind === kind && a.bound_to === productId))
            return;
        accessories.push({ id, kind, name, scope: 'site', area_id: '', colour: null, on: true, bound_to: productId, run_ids: [],
            unit: kind === 'underlay' ? 'm²' : 'LM', quantity: round(quantity), rate: null,
            note: kind === 'trims' ? 'Naplock or Ripple Bar to suit the measured transitions.' : 'Included with the carpet product.',
            product_id: type?.product_id ?? null, variant_id: variantId ?? null });
    };
    const underlay = catalogue.find(x => /carpet underlay/i.test(x.name));
    const eight = underlay?.variants.find(v => /\b8\s*mm\b/i.test(`${v.label} ${v.spec ?? ''}`));
    add('underlay', eight?.label ?? '8mm Carpet Underlay', m.floorAreaM2, underlay, eight?.variant_id);
    const smooth = catalogue.find(x => /smooth\s*edges?/i.test(x.name));
    const smoothVariant = smooth?.variants.find(v => v.is_default) ?? smooth?.variants[0];
    add('smoothedge', 'New or replacing smoothedge', m.skirtingRunM, smooth, smoothVariant?.variant_id);
    add('trims', 'Carpet trims', m.trimRunM);
    const services = [...doc.services];
    if (freeCarpetRemovalEligible(doc.assessment) && !services.some(s => s.service_id === 'remove_dispose')) {
        services.push({ id: 'carpet-default-removal', service_id: 'remove_dispose', name: 'Remove & dispose existing carpet', unit: 'm²',
            quantity: round(m.floorAreaM2), rate: 0, optional: false, variation: false, offered: true, chosen_option: 'free_carpet',
            options: [], note: 'Included with the carpet installation.', source: 'canvas' });
    }
    if (m.stairSteps > 0 && !services.some(s => s.service_id === 'carpet_stair_install' || s.service_id === 'stair_work')) {
        services.push({ id: 'carpet-default-stairs', service_id: 'carpet_stair_install', name: 'Standard carpet stair installation', unit: 'step',
            quantity: m.stairSteps, rate: 25, optional: false, variation: false, offered: true, chosen_option: 'standard', options: [],
            note: 'Carpet falls under the nosing without wrapping it. Stair material is included in the carpet order.', source: 'canvas' });
    }
    return { ...doc, pricing, accessories, services };
}
/** Keep included underlay on measured floor area when carpet order length changes. */
export function reconcileCarpetDefaults(doc, pricing, m) {
    const boundProducts = new Set(doc.accessories.filter(a => a.id.startsWith('carpet-default-underlay:')).map(a => a.bound_to).filter(Boolean));
    pricing = ProposalPricing.parse({ ...pricing, products: pricing.products.map(p => boundProducts.has(p.productId) && p.quantity_source !== 'manual' ? { ...p, area: round(m.floorAreaM2) } : p) });
    const active = new Set(pricing.products.map(p => p.productId));
    const accessories = doc.accessories.filter(a => !a.id.startsWith('carpet-default-') || active.has(a.bound_to ?? ''))
        .map(a => {
        if (a.quantity_source === 'manual' || !a.id.startsWith('carpet-default-underlay:'))
            return a;
        const selection = pricing.products.find(p => p.productId === a.bound_to);
        if (!selection)
            return a;
        return { ...a, quantity: round(m.floorAreaM2) };
    });
    const hasDefault = accessories.some(a => a.id.startsWith('carpet-default-'));
    const services = hasDefault ? doc.services : doc.services.filter(s => !s.id.startsWith('carpet-default-'));
    return { pricing, accessories, services };
}
/** Refresh an existing carpet package after a new canvas assessment is imported. */
export function refreshCarpetMeasures(doc, m) {
    const order = doc.assessment.carpet_layout ? carpetOrderSummary(doc.assessment.carpet_layout) : null;
    const boundProducts = new Set(doc.accessories.filter(a => a.id.startsWith('carpet-default-')).map(a => a.bound_to).filter(Boolean));
    const pricing = doc.pricing ? ProposalPricing.parse({ ...doc.pricing, products: doc.pricing.products.map(p => p.quantity_source !== 'manual' && (boundProducts.has(p.productId) || p.carpetRollWidthM !== undefined) ? { ...p, area: round(m.floorAreaM2), ...(order ? { quantity: order.wholeLm, carpetRollWidthM: doc.assessment.carpet_layout.roll_width_m } : {}) } : p) }) : undefined;
    const accessories = doc.accessories.map(a => {
        if (a.quantity_source === 'manual')
            return a;
        if (a.id.startsWith('carpet-default-underlay:')) {
            return { ...a, quantity: round(m.floorAreaM2) };
        }
        if (a.id.startsWith('carpet-default-trims:'))
            return { ...a, quantity: round(m.trimRunM) };
        if (a.id.startsWith('carpet-default-smoothedge:'))
            return { ...a, quantity: round(m.skirtingRunM) };
        return a;
    });
    const services = doc.services.map(s => s.quantity_source === 'manual' ? s : s.id === 'carpet-default-removal' ? { ...s, quantity: round(m.floorAreaM2) } :
        s.id === 'carpet-default-stairs' ? { ...s, quantity: m.stairSteps } : s);
    return { ...doc, pricing, accessories, services };
}
//# sourceMappingURL=carpet-defaults.js.map